import { useCallback, useState } from 'react';

import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import dayjs from 'src/helpers/dayjs';
import { useQrStopTimerScan } from 'src/hooks/useQrStopTimerScan';
import { ECheckinTimerType, ICheckin } from 'src/interfaces/Checkin';
import useCheckinsStore from 'src/stores/checkinsStore';

interface Params {
  editionId: string | undefined;
  competitionId: string | undefined;
  checkins: ICheckin[];
}

export function useQrStopTimerFlow({ editionId, competitionId, checkins }: Params) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const doHandleTimer = useCheckinsStore(s => s.handleTimer);
  const doFetchCheckinsByCompetitionId = useCheckinsStore(s => s.fetchCheckinsByCompetitionId);

  const [qrStopScannerOpen, setQrStopScannerOpen] = useState(false);

  const handleQrStopTimer = useCallback(
    async (checkinId: string, participantName: string) => {
      if (!competitionId || !editionId) return;

      const { confirmed } = await confirm({
        title: t('timers.qrStopConfirmTitle', { name: participantName }),
        description: t('timers.qrStopConfirmDescription'),
        confirmationText: t('timers.stopConfirm'),
        cancellationText: t('timers.cancel'),
      });
      if (!confirmed) {
        enqueueSnackbar(t('timers.startFinishError'), {
          variant: 'error',
        });
        return;
      }
      await doHandleTimer({
        id: checkinId,
        type: ECheckinTimerType.finish,
        timeStamp: dayjs().toDate(),
      });
      await doFetchCheckinsByCompetitionId(editionId, competitionId);
      enqueueSnackbar(t('timers.startFinishSuccess'), {
        variant: 'success',
      });
    },
    [
      competitionId,
      confirm,
      doFetchCheckinsByCompetitionId,
      doHandleTimer,
      editionId,
      enqueueSnackbar,
      t,
    ],
  );

  const { handleScan: handleQrStopTimerScan } = useQrStopTimerScan({
    editionId,
    checkins,
    onResolved: ({ checkinId, participantName }) => {
      void handleQrStopTimer(checkinId, participantName);
    },
    enqueueSnackbar,
    t,
    onSuccess: () => setQrStopScannerOpen(false),
  });

  return {
    qrStopScannerOpen,
    openQrStopScanner: () => setQrStopScannerOpen(true),
    closeQrStopScanner: () => setQrStopScannerOpen(false),
    handleQrStopTimerScan,
  };
}
